import React, { useState, useEffect } from "react";
import useAxios from "../../hooks/useAxios";
import { EpujaOrder, EpujaAssignPandit } from "../../types/interfaces";
import { ContentLoader } from "../common";

type ModalProps = {
  show: boolean;	
  order_id: string;
  handleModalOpenClose: (show: boolean) => void;
};

const EpujaOrderDetail: React.FC<ModalProps> = ({ show, order_id, handleModalOpenClose }) => {
  const axiosInstance = useAxios();	
  const [order, setOrder] = useState<EpujaOrder | null>(null);
  const [pandits, setPandits] = useState<EpujaAssignPandit[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const getOrderData = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const response: any = await axiosInstance({
        url: "epuja-orders/detail",
        method: "GET",
        params: { orderId: order_id },
      });
      console.log("orders details response", response);
      setOrder(response.data.order);
      setPandits(response.data.allpandits);
    } catch (error: any) {
      console.error("Error in api request:", error);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
   }, [axiosInstance, order_id]);
  
  useEffect(() => {
    if (show) {
      getOrderData();
    } else {
      setOrder(null);
    }
  }, [show, getOrderData]);
  
  const handleClose = () => {
    handleModalOpenClose(false);
  };
  

  const getPaymentBadge = () => {
    switch (order.payment_status) {
      case "Paid":
      case "Upfront":
        return "badge-light-success";
      case "Pending":
        return "badge-light-warning";
      case "Failed":
        return "badge-light-danger";
      default:
        return "badge-light-secondary";
    }
  };

  const getStatusBadge = () => {
    switch (order.status) {
      case "Completed":
        return "badge-light-success";
      case "Processing":
        return "badge-light-primary";
      case "Pending":
        return "badge-light-warning";
      case "Canceled":
        return "badge-light-danger";
      default:
        return "badge-light-secondary";
    }
  };

  const getAssignedStatusBadge = () => {
    switch (order.assignedpandit.status) {
      case "Accepted":
        return "badge-light-success";
      case "Assigned":
        return "badge-light-primary";
      case "Rejected":
        return "badge-light-warning";
      case "Canceled":
        return "badge-light-danger";
      default:
        return "badge-light-secondary";
    }
  };

  //if(isLoading) return <ContentLoader />;
  
  return (
    <>
      <div
        className="modal fade show"
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable animate__animated animate__fadeInDown">
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <h2 className="fw-bold">E-Puja Order Details</h2>
              <button
                type="button"
                className="btn btn-icon btn-sm btn-active-light-primary"
                onClick={handleClose}
              >
                <i className="ki-outline ki-cross fs-1" />
              </button>
            </div>
            <div className="modal-body pt-4">
              <div className="row g-6 align-items-stretch">
                {isLoading || !order ? (
				    <div className={`card ${isLoading ? "overlay overlay-block" : ""}`} style={{ minHeight: '300px' }}>
					  <ContentLoader />
					</div>
				) : (
                <>
                <div className="col-lg-6 d-flex">
                  <div className="card card-flush shadow-sm h-100 w-100">
                    <div
                      className="card-header d-flex align-items-center justify-content-between cursor-pointer"
                      data-bs-toggle="collapse"
                      data-bs-target="#order_info"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ki-outline ki-information-5 fs-2 text-primary" />
                        <h3 className="card-title fw-bold mb-0">Order Info</h3>
                      </div>
                      <span className="btn btn-icon btn-sm btn-active-light-primary">
                        <i className="ki-outline ki-down fs-3 rotate-180 transition" />
                      </span>
                    </div>

                    <div id="order_info" className="collapse show">
                      <div className="card-body fs-6">
                        <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Order No</span>
                          <span className="fw-semibold">{order.order_number}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Booked On</span>
                          <span className="fw-semibold">
                            {new Date(order.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Puja Date</span>
                          <span className="fw-semibold">
                            {order.puja_date
                              ? new Date(order.puja_date).toLocaleDateString()
                              : "Not Scheduled"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                
                <div className="col-lg-6 d-flex">
                  <div className="card card-flush shadow-sm h-100 w-100">
                    <div
                      className="card-header d-flex align-items-center justify-content-between cursor-pointer"
                      data-bs-toggle="collapse"
                      data-bs-target="#payment_info"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ki-outline ki-wallet fs-2 text-success" />
                        <h3 className="card-title fw-bold mb-0">Payment Info</h3>
                      </div>
                      <span className="btn btn-icon btn-sm btn-active-light-primary">
                        <i className="ki-outline ki-down fs-3 rotate-180 transition" />
                      </span>
                    </div>

                    <div id="payment_info" className="collapse show">
                      <div className="card-body fs-6">
                        <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Total Amount</span>
                          <span className="fw-semibold">
                            Rs. {order.total_amount || 0}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Paid Amount</span>
                          <span className="fw-semibold">
                            Rs.{" "}
                            {order.payment_status === "Upfront"
                              ? order.upfront_amount
                              : order.total_amount}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Payment Status</span>
                          <span className={`badge ${getPaymentBadge()}`}>
                            {order.payment_status}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Puja Status</span>
                          <span className={`badge ${getStatusBadge()}`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                
                <div className="col-lg-6 d-flex">
                  <div className="card card-flush shadow-sm h-100 w-100">
                    <div
                      className="card-header d-flex align-items-center justify-content-between cursor-pointer"
                      data-bs-toggle="collapse"
                      data-bs-target="#user_info"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ki-outline ki-profile-circle fs-2 text-info" />
                        <h3 className="card-title fw-bold mb-0">User Info</h3>
                      </div>
                      <span className="btn btn-icon btn-sm btn-active-light-primary">
                        <i className="ki-outline ki-down fs-3 rotate-180 transition" />
                      </span>
                    </div>

                    <div id="user_info" className="collapse show">
                      <div className="card-body fs-6">
                        <div className="fw-bold mb-1">
                          {typeof order.created_by === "object"
                            ? order.created_by.fullname
                            : "N/A"}
                        </div>
                        <div className="text-muted">{order.email}</div>
                        <div className="text-muted">{order.phone_no}</div>
                      </div>
                    </div>
                  </div>
                </div>

                
                <div className="col-lg-6 d-flex">
                  <div className="card card-flush shadow-sm h-100 w-100">
                    <div
                      className="card-header d-flex align-items-center justify-content-between cursor-pointer"
                      data-bs-toggle="collapse"
                      data-bs-target="#pandit_info"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ki-outline ki-user fs-2 text-warning" />
                        <h3 className="card-title fw-bold mb-0">Pandit Info</h3>
                      </div>
                      <span className="btn btn-icon btn-sm btn-active-light-primary">
                        <i className="ki-outline ki-down fs-3 rotate-180 transition" />
                      </span>
                    </div>

                    <div id="pandit_info" className="collapse show">
                      <div className="card-body">
                        {order.pandit_id ? (
                          <>
						    <div className="d-flex align-items-center gap-4">
								<div className="symbol symbol-60px symbol-circle shadow-sm">
								  <img src={order.pandit_id.imagepath} alt="pandit-profile" />
								</div>
								<div>
								  <div className="fw-bold fs-5">
									{order.pandit_id.fullname}
								  </div>
								  <div className="text-muted">Assigned Pandit</div>
								</div>
							</div>
							<br />
							<div className="d-flex justify-content-between">
							  <span className="text-muted">Pandit Status</span>
							  <span className={`badge ${getAssignedStatusBadge()}`}>
								{order.assignedpandit.status}
							  </span>
							</div>
                          </>
                        ) : (
                          <div className="text-muted">Not Assigned Yet</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="card card-flush shadow-sm">
                    <div
                      className="card-header d-flex align-items-center justify-content-between cursor-pointer"
                      data-bs-toggle="collapse"
                      data-bs-target="#additional_info"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ki-outline ki-notepad fs-2 text-dark" />
                        <h3 className="card-title fw-bold mb-0">Additional Info</h3>
                      </div>
                      <span className="btn btn-icon btn-sm btn-active-light-primary">
                        <i className="ki-outline ki-down fs-3 rotate-180 transition" />
                      </span>
                    </div>
                    <div id="additional_info" className="collapse show">
                      <div className="card-body fs-6">
					    <div className="row">
							<div className="col-lg-6 "> 
								<div className="mb-4">
								  <div className="fw-semibold mb-1">User Message</div>
								  <div className="text-muted">{order.message || "—"}</div>
								</div>
								<div>
								  <div className="fw-semibold mb-1">Notes</div>
								  <div className="text-muted">{order.notes || "—"}</div>
								</div>
							</div>
							<div className="col-lg-6 d-flex flex-column border-start">
							  <div className="fw-semibold mb-2">Pandit History</div>

							  <div
								className="pandit-history-scroll pe-2"
								style={{ maxHeight: "320px", overflowY: "auto" }}
							  >
								{pandits
								  ?.filter(
									(item) =>
									  item?.pandit_id?._id &&
									  order?.pandit_id?._id &&
									  item.pandit_id._id !== order.pandit_id._id
								  )
								  .map((pandit) => (
									<div
									  key={pandit._id}
									  className="d-flex align-items-center justify-content-between border rounded p-3 mb-2"
									>
									  {/* LEFT: Pandit info */}
									  <div className="d-flex align-items-center gap-3">
										<div className="symbol symbol-50px symbol-circle shadow-sm">
										  <img
											src={pandit?.pandit_id?.imagepath}
											alt="pandit-profile"
										  />
										</div>

										<div>
										  <div className="fw-bold">
											{pandit?.pandit_id?.fullname}
										  </div>
										  <div className="text-muted fs-8">
											{pandit?.pandit_id?.phone_no}
										  </div>
										</div>
									  </div>

									  {/* RIGHT: Message */}
									  <div className="text-end ms-3" style={{ maxWidth: "45%" }}>
										<div className="text-muted fs-8">Pandit Message</div>
										<div className="fw-semibold text-truncate">
										  {pandit?.reject_message || "-"}
										</div>
									  </div>
									</div>
								  ))}
							  </div>
							</div>
						 </div>	
                      </div>
                    </div>
                  </div>
                </div>
				</>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show" />
    </>
  );
};

export default EpujaOrderDetail;
